/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/**
 * Controls whether the add-in uses direct WOPI PutFile (Mode A, native WOPI)
 * or the Workspace API (Mode B, add-in mediated saves).
 *
 * Mode A requires CSPP (Cloud Storage Partner Program) registration with
 * Microsoft. Without CSPP, desktop Word does not enter native WOPI edit mode,
 * and direct browser-to-WOPI-host fetch calls fail with CORS (the WOPI host
 * serves server-to-server requests from native Word, not browser clients).
 *
 * Set to true ONLY after:
 * 1. CSPP registration is approved
 * 2. The WOPI host is registered with Microsoft
 * 3. Desktop Word enters native WOPI edit mode for Ecsper documents
 *
 * Target: post ISO 27001 (September 2026)
 */
export const CSPP_REGISTERED = false;

/**
 * Detect whether the current document was opened from the Ecsper WOPI host.
 * If so, extract the file ID and access token for PutFile calls.
 *
 * Detection order:
 *   1. {@link Office.context.document.url} for the WOPI host domain (primary)
 *   2. Task pane URL params `wopiFileId`, `wopiToken`, `wopiHost` (fallback when
 *      Office strips query params from the document URL, per ECS-RPT-MS365-AUTOBIND-FIX-001)
 *
 * The domain defaults to `dev.wopi.ecsper.io` and is configured via `VITE_WOPI_HOST`.
 *
 * When {@link CSPP_REGISTERED} is false, always returns Mode B (Workspace API).
 */

const WOPI_HOST_DOMAIN = import.meta.env.VITE_WOPI_HOST || 'dev.wopi.ecsper.io';

export interface WopiContext {
  isWopiMode: true;
  fileId: string;
  accessToken: string;
  wopiBaseUrl: string;
}

export interface NonWopiContext {
  isWopiMode: false;
}

export type WopiModeResult = WopiContext | NonWopiContext;

export interface DetectWopiModeOptions {
  /** Override for unit tests. Defaults to {@link Office.context.document.url}. */
  documentUrl?: string | null;
  /** Override task pane search string. Defaults to `window.location.search`. */
  taskPaneSearch?: string;
}

function hostnameMatchesWopiHost(hostname: string): boolean {
  return hostname === WOPI_HOST_DOMAIN || hostname.endsWith(`.${WOPI_HOST_DOMAIN}`);
}

function parseWopiDocumentUrl(docUrl: string): WopiContext | null {
  const url = new URL(docUrl);
  if (!hostnameMatchesWopiHost(url.hostname)) {
    return null;
  }

  const pathMatch = url.pathname.match(/\/wopi\/files\/([^/]+)/);
  if (!pathMatch) {
    return null;
  }

  const fileId = pathMatch[1] ?? '';
  const accessToken = url.searchParams.get('access_token') ?? '';

  if (fileId === '' || accessToken === '') {
    return null;
  }

  return {
    isWopiMode: true,
    fileId,
    accessToken,
    wopiBaseUrl: `${url.protocol}//${url.hostname}`,
  };
}

function parseWopiTaskPaneParams(search: string): WopiContext | null {
  const params = new URLSearchParams(search);
  const fileId = params.get('wopiFileId') ?? '';
  const accessToken = params.get('wopiToken') ?? '';
  const wopiHost = params.get('wopiHost') ?? WOPI_HOST_DOMAIN;

  if (fileId === '' || accessToken === '') {
    return null;
  }

  return {
    isWopiMode: true,
    fileId,
    accessToken,
    wopiBaseUrl: `https://${wopiHost}`,
  };
}

export function detectWopiMode(options: DetectWopiModeOptions = {}): WopiModeResult {
  // Mode B: CSPP not registered. All saves go through the Workspace API.
  // Direct WOPI PutFile from the browser fails with CORS.
  if (!CSPP_REGISTERED) {
    return { isWopiMode: false };
  }

  // Mode A: CSPP registered. Detect WOPI from document URL.
  try {
    const docUrl = options.documentUrl ?? Office.context.document.url;
    if (docUrl !== null && docUrl !== undefined && docUrl !== '') {
      const fromDocument = parseWopiDocumentUrl(docUrl);
      if (fromDocument !== null) {
        return fromDocument;
      }
    }

    const search =
      options.taskPaneSearch ?? (typeof window !== 'undefined' ? window.location.search : '');
    const fromTaskPane = parseWopiTaskPaneParams(search);
    if (fromTaskPane !== null) {
      return fromTaskPane;
    }

    return { isWopiMode: false };
  } catch {
    return { isWopiMode: false };
  }
}
